import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const VIDEO_EXTENSION_PATTERN = /(\.mp4|\.webm|\.mkv|\.m4v|\.mov|\.avi|\.m3u8)(\?|$)/i;
const FALLBACK_EXTENSION_BY_MEDIA_TYPE = {
  video: 'mp4',
  image: 'jpg',
};

const sanitizeFileSegment = (value, fallback = 'sighting') => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

  return normalized || fallback;
};

const extractUrlPathname = (url) => {
  if (typeof url !== 'string' || url.trim().length === 0) {
    return '';
  }

  try {
    return new URL(url).pathname || '';
  } catch (error) {
    return url.split('?')[0] || '';
  }
};

const inferFileExtension = (url, mediaType = 'image') => {
  const pathname = extractUrlPathname(url);
  const match = pathname.match(/\.([a-z0-9]+)$/i);
  if (match?.[1]) {
    return match[1].toLowerCase();
  }

  return FALLBACK_EXTENSION_BY_MEDIA_TYPE[mediaType] || 'bin';
};

export function isLikelyVideoUrl(url) {
  if (typeof url !== 'string') {
    return false;
  }
  return VIDEO_EXTENSION_PATTERN.test(url);
}

export function getEntryDownloadUrl(entry) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  if (entry.mediaType === 'video') {
    return entry.videoUrl || entry.mediaUrl || null;
  }

  return entry.mediaUrl || null;
}

export function buildEntryDownloadFilename(entry, url = getEntryDownloadUrl(entry)) {
  const speciesSegment = sanitizeFileSegment(entry?.species, 'sighting');
  const captureTimestamp = entry?.spottedAt || entry?.createdAt || null;
  const timestampSegment =
    captureTimestamp instanceof Date && !Number.isNaN(captureTimestamp.getTime())
      ? captureTimestamp.toISOString().replace(/[:.]/g, '-')
      : new Date().toISOString().replace(/[:.]/g, '-');
  const extension = inferFileExtension(url, entry?.mediaType || 'image');

  return `${speciesSegment}-${timestampSegment}.${extension}`;
}

export async function downloadEntryMedia(entry) {
  const sourceUrl = getEntryDownloadUrl(entry);
  if (!sourceUrl) {
    throw new Error(
      entry?.mediaType === 'video'
        ? 'No video is available to download.'
        : 'No HD image is available to download.',
    );
  }

  const filename = buildEntryDownloadFilename(entry, sourceUrl);
  if (!entry?.meta?.parentPath) {
    throw new Error('This sighting is missing its download details. Refresh the page and try again.');
  }

  let result;
  try {
    // The function authorizes a short-lived storage link; it never transfers
    // the file. The browser owns the download from the first byte onward.
    const createDownloadLink = httpsCallable(functions, 'createSightingDownloadLink');
    result = await createDownloadLink({
      parentPath: entry.meta.parentPath,
      speciesDocPath: entry.meta.speciesDocPath || '',
      sourceUrl,
      filename,
    });
  } catch (error) {
    const code = String(error?.code || '');
    if (code === 'functions/unauthenticated') {
      throw new Error('Your session expired. Log in again to download this sighting.');
    }
    if (code === 'functions/permission-denied') {
      throw new Error('Access to this sighting was denied. Please refresh the page and try again.');
    }
    if (code === 'functions/not-found') {
      throw new Error('This sighting or media file is no longer available.');
    }
    if (code === 'functions/failed-precondition' || code === 'functions/invalid-argument') {
      throw new Error(error.message || 'This sighting is not ready to download.');
    }
    throw new Error('Unable to prepare the download. Please try again.');
  }

  let downloadUrl;
  try {
    downloadUrl = new URL(result?.data?.url);
    if (downloadUrl.protocol !== 'https:' || downloadUrl.username || downloadUrl.password) {
      throw new Error('Invalid download URL');
    }
  } catch (error) {
    throw new Error('Unable to prepare the download. Please try again.');
  }

  // Storage sets Content-Disposition: attachment, so this also works across
  // origins. Staying in this tab avoids popup blockers after the signing call.
  const link = document.createElement('a');
  link.href = downloadUrl.href;
  link.download = filename;
  link.referrerPolicy = 'no-referrer';
  link.hidden = true;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
  }
  // This means handed to the browser, not that the file finished downloading.
  return { filename, url: sourceUrl, native: true };
}

export default isLikelyVideoUrl;
